import * as Sentry from "@sentry/react";

const FILTERED = "[Filtered]";

const SENSITIVE_KEY_PARTS = [
  "authorization",
  "cookie",
  "password",
  "passwd",
  "secret",
  "token",
  "apikey",
  "privatekey",
  "serviceaccount",
  "razorpaysignature",
  "paymentsignature",
  "documentcontent",
  "documentbody",
  "requestbody",
  "content",
  "body",
  "document",
  "filebytes",
];

function normalizedKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function isSensitiveKey(key: string): boolean {
  const normalized = normalizedKey(key);
  return SENSITIVE_KEY_PARTS.some((part) => normalized.includes(part));
}

function stripUrlQuery(value: string): string {
  if (!/^https?:\/\//i.test(value)) return value;

  try {
    const url = new URL(value);
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return value;
  }
}

function scrubValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(scrubValue);
  if (typeof value === "string") return stripUrlQuery(value);
  if (value === null || typeof value !== "object") return value;

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, child]) => [
      key,
      isSensitiveKey(key) ? FILTERED : scrubValue(child),
    ])
  );
}

export function scrubSentryEvent<T>(event: T): T {
  return scrubValue(event) as T;
}

function cappedTraceSampleRate(value: number | undefined): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(value ?? 0, 0), 0.05);
}

export function initializeSentry(): boolean {
  const config = window.__VEDRA_RUNTIME_CONFIG__;
  if (!config?.sentryDsn) return false;

  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.sentryEnvironment || "unknown",
    release: config.sentryRelease || undefined,
    tracesSampleRate: cappedTraceSampleRate(config.sentryTracesSampleRate),
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    beforeSend: scrubSentryEvent,
    beforeSendTransaction: scrubSentryEvent,
  });
  return true;
}

export { Sentry };
